<template>
  <modal
    v-model="show"
    :footer="!saving"
    @hide="closeModal"
    title="Save Resource to Library">
    <ChooseStudentForm
      v-if="!saving && show"
      :preselected-student="selectedStudent"
      :all-students="students"
      :recent-students="recentStudents"
      @selectedStudent:existing="choseStudent"
      @selectedStudent:new="choseStudent"
    />

    <template v-if="saving">
      <p class="text-muted"><i class="fa fa-circle-o-notch fa-spin"/> Saving...</p>
    </template>

    <alert
      type="success"
      :dismissible="true"
      @dismissed="showSaveSuccess = !showSaveSuccess"
      v-if="showSaveSuccess">
      <p>Resource was saved to <router-link
        :to="{name: 'StudentLibraryPage', params: {uuid: saveResult.uuid}}">{{ saveResult.name }}</router-link></p>
    </alert>

    <alert
      type="danger"
      :dismissible="true"
      @dismissed="showError = !showError"
      v-if="showError">
      <p>{{ saveError }}</p>
    </alert>

    <div
      v-if="!saving"
      class="btn-toolbar"
      slot="footer">
      <button
        v-if="selectedStudent && saveResult && selectedStudent.uuid !== saveResult.uuid || !saveResult"
        class="btn btn-success btn-lg"
        :disabled="!selectedStudent"
        @click="save">
        Save to {{ selectedStudent && selectedStudent.name || 'a folder' }}
      </button>
      <button
        v-if="!saveResult"
        class="btn btn-link btn-lg"
        @click="closeModal()">Cancel</button>
      <button
        v-if="saveResult"
        class="btn btn-default btn-lg"
        @click="closeModal()">Done</button>
    </div>
  </modal>
</template>

<script>
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'
import ChooseStudentForm from '@/components/library/ChooseStudentForm'
import { addStudent, postStudentContent } from '@/api/library'
import { mapState, mapActions } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'

export default {
  name: 'SaveResourceModal',
  mixins: [libraryQuotasMixin],
  components: {
    ChooseStudentForm
  },
  props: {
    contentUuid: {
      type: String,
      required: true
    },
    open: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapState('library', ['students', 'recentStudents']),
    ...mapState('user', ['user'])
  },
  data() {
    return {
      show: this.open,
      selectedStudent: null,
      saving: false,
      showSaveSuccess: false,
      showError: false,
      saveError: '',
      saveResult: null
    }
  },
  methods: {
    ...mapActions('library', ['fetchStudents', 'fetchRecentStudents']),
    closeModal() {
      this.$emit('update:open', false)
    },
    choseStudent(student) {
      this.selectedStudent = student
    },
    async save() {
      if (!this.selectedStudent) {
        return
      }
      if (
        this.selectedStudent.uuid &&
        this.maxContentReachedForStudent(CONTENT_TYPES.RESOURCE, this.selectedStudent.uuid)
      ) {
        this.showError = true
        this.saveError = this.maxContentTypeErrorMessage(CONTENT_TYPES.RESOURCE)
        this.sendMaxContentInFolderEvent(CONTENT_TYPES.RESOURCE, this.selectedStudent.uuid)
        return
      }

      let isNewStudent = !this.selectedStudent.uuid
      this.saving = true
      this.showError = false
      this.showSaveSuccess = false

      try {
        if (isNewStudent) {
          let newStudentResp = await addStudent(this.selectedStudent.name, this.user.uuid)
          this.selectedStudent.uuid = newStudentResp.uuid
        }
        let resp = await postStudentContent(
          this.selectedStudent.uuid,
          CONTENT_TYPES.RESOURCE,
          this.contentUuid,
          this.user.uuid
        )
        this.saveResult = resp.collection
        this.saving = false
        this.showSaveSuccess = true
        this.fetchRecentStudents(this.user.uuid)
        this.fetchStudents(this.user.uuid)
        this.$sendEvent('Resource Saved to Library', {
          student_uuid: this.saveResult.uuid
        })
      } catch (err) {
        this.showError = true
        this.saveError = (err && err.message) || err
        console.error(err)
      }
    }
  },
  watch: {
    open() {
      this.show = this.open
      this.selectedStudent = (this.recentStudents && this.recentStudents[0]) || null
    }
  },
  eventContext() {
    return {
      resource_uuid: this.contentUuid
    }
  }
}
</script>

<style lang="less">
.close {
  color: var(--gray-weak);
  opacity: 1;

  &:hover {
    color: var(--gray-default);
    opacity: 1;
  }

  &:focus {
    opacity: 1;
  }
}
</style>
